/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {getMenuStyles} from '../../filters/FilterHelpers';
import Hidden from '../../layout/Hidden';
import {DLSTheme, FilterPropsType} from '../../types/types';
import BoundaryFilterTools from './BoundaryFilterTools';
import FilterMegaMenu from './FilterMegaMenu';
import MobileActionContent from './MobileActionContent';

export interface BoundaryFilterProps extends FilterPropsType {
  elementId?: string;
  minMenuWidth?: string;
  menuWidth?: string;
  menuLeft?: string;
  onBoundaryChoose: Function;
  onClear: Function;
  disableClear?: boolean;
  onCancel?: () => void;
  onMenuClose?: () => void;
  selectFieldMinWidth?: string;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
}));

/**
 * BoundaryFilter Menu that utilizes the FilterMegaMenu, It will
 * give option to create a new boundary including a radial, polygon, or draw.
 *
 * ```typescript
 *
 * import {BoundaryFilter} from '@ion/dls/src/components/menus';
 *
 * <BoundaryFilter elementId={PendoIds.assetHistoryBoundaryFilter}
 *   menuWidth='25%' /> *
 * ```
 */
export const BoundaryFilter = ({
  elementId,
  minMenuWidth,
  onClear,
  onBoundaryChoose,
  onCancel,
  onMenuClose,
  selectFieldMinWidth,
  disableClear = false,
  isMobileView = false
}: BoundaryFilterProps) => {
  const classes                   = useStyles();
  const [isMenuOpen, setMenuOpen] = useState(true);

  const {
    menuLeft,
    menuWidth
  } = getMenuStyles(isMobileView);

  const onClearClick = useCallback(() => {
    setMenuOpen(false);
    onClear();
  }, []);

  const handleExitBoundaryFilter = useCallback(() => {
    onMenuClose?.();
    onCancel?.();
  }, []);

  return (
    <>
      <Hidden show='desktop'>
        <FilterMegaMenu elementId={elementId}
          hideActionButtons
          menuLeft={menuLeft}
          selectFieldValue={translate('common:filters.boundary')}
          menuWidth={menuWidth}
          selectFieldMinWidth={selectFieldMinWidth}
          shouldCloseMenu={!isMenuOpen}
          setMenuCurrentState={setMenuOpen}
          minMenuWidth={minMenuWidth}>
          <BoundaryFilterTools handleExitBoundaryFilter={handleExitBoundaryFilter}
            onClearClick={onClearClick}
            onBoundaryChoose={onBoundaryChoose}
            disableClear={disableClear}
            setMenuOpen={setMenuOpen}/>
        </FilterMegaMenu>
      </Hidden>
      <Hidden show='mobile'>
        <MobileActionContent hideActionButtons>
          <BoundaryFilterTools handleExitBoundaryFilter={handleExitBoundaryFilter}
            onClearClick={onClearClick}
            onBoundaryChoose={onBoundaryChoose}
            disableClear={disableClear}
            setMenuOpen={setMenuOpen}/>
        </MobileActionContent>
      </Hidden>
    </>
  );
};

export default memo(BoundaryFilter);
